// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import { executePolling } from '../../../../../share/util/api/polling';
import { executeSyncRequest } from '../../../../../share/util/api/syncRequest';
import { apiErrorDefaultAction } from '../../../../../share/util/api/errorAction';
import { OBD_PERMANENT_DTC } from '../../../../../constants/usageCountTypes';
import apiService from '../../../../../share/util/api/service';
import alert from '../../../../../share/alert';
import loading from '../../../../../share/components/vue/ProcessingIcon';
import util from '../../../../../share/util/utils';
import eventLogUtil from '../../../../../share/util/eventLogUtil';
import cstmTable from '../../../../../share/components/vue/PaginationTable';
import ffdModal from '../../../../vue/FFDModal';
import diagnoseResource from '../../../../../share/util/diagnose';
import memoPage from '../../../../vue/MemoPage';
import customProgress from "../../../../../share/components/vue/CustomProgress";
import { pushFunctionList } from '../../../../../router/obd';
import { createObdDtcTable } from '../../../../../util/obd/diag';
import { getMeasuredDataName, getCsvFileName } from '../../../../../util/fileExport';

export default {
  name: "obdPermanentDtc",
  data() {
    return {
      processing: false,
      measuredId: null,
      getDiagCodeListResponse: null,
      dtcSystemTableColumns: [],
      dtcListItems: [],
      isDisabledMemo: false,
      isObd: true,
      // ボタン消去
      isInvisibleChainButton: true,
      isInvisibleClearDtcButton: true,
      // vue warn回避
      readinessCodeTable: [],
      readinessCodeTableColumns: [],
      isVisibleReadinessCodeButton: false,
      isEnableClearDtc: true,
      modalFfd: false,
      modalReadiness: false,
      modalMemo: false,
      readinessCodeInputDtc: '',
      readinessCodeValidateDtc: {},
      isMemoDialogLoading: false,
      ffdTitle: '',
      ffdSystem: '',
      ffdDescription: '',
      ffdSystemTable: [],
      selectedSystem: null,
      progress: 0,
      phaseMessage: '',
      modalProgress: false,
    };
  },

  props: {
    obdFunctionLabelKey: { type: String, required: false, default: 'fixed_resource.obd_menu' },
    functionNameKey: { type: String, required: false, default: 'fixed_resource.obd_menu_pdtc' },
    destinationAPI: { type: String, required: false, default: apiService.OBD },
  },

  components: {
    'processing-view': loading,
    'cstm-table': cstmTable,
    'memo-Page': memoPage,
    'ffd-modal': ffdModal,
    // vue warn 回避
    'custom-Progress': customProgress,
  },

  watch: {
    language: {
      /**
       * 言語変更を監視する
       */
      handler(val) {
        val; // To avoid warning
        this.updateColumns();
        this.updateDTCTable();
      }
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('observation', ['usageCountUp']),

    /**
     * componentのmounted時に呼び出す処理
     */
    mountedLogic() {
      this.dtcListItems = [];
      this.getDiagCodeRequest();
      // パスに"menu"を含む場合はメモボタンを有効化
      if (!this.$route.path.includes('menu')) {
        this.isDisabledMemo = true;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * 非同期でダイアグ取得を実行し、テーブル更新を行う
     * @param {boolean} eventLogHandleIdFlg 再取得から呼ばれた際に true（イベントログの操作 ID 発行する）とする
     * @returns なし
     */
    async getDiagCodeRequest(eventLogHandleIdFlg = false) {
      try {
        this.processing = true;
        const systemNo = this.$route.params.systemNo;
        if (eventLogHandleIdFlg) {
          // イベントログの操作 ID 発行
          await eventLogUtil.createEventLogHandleId(this.netAppId);
        }
        const response = await executePolling(
          this,
          // POST API 呼び出しのコールバック
          async () => {
            return await apiService.postCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${systemNo}/permanentDtc`
            );
          },
          // GET API 呼び出しのコールバック
          async (postApiResponse) => {
            return await apiService.getCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/requests/${postApiResponse.data.request_id}`,
              this.operationScreen
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // GET API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // OBDシステムの利用回数カウントアップ
          this.usageCountUp({ type: OBD_PERMANENT_DTC });
          // 正常終了：ダイアグコードの一覧取得を行う
          this.measuredId = response.data.results.measure_id;
          this.getDiagCodeListResponse = await this.getDiagCodeList();
          // responseが存在しない場合、処理終了
          if (!this.getDiagCodeListResponse) {
            return;
          }
          this.updateDTCTable();
          this.updateColumns();
        }
      } finally {
        this.processing = false;
      }
    },

    /**
     * 一覧取得処理を行う関数
     * @returns GetのResponse | null
     */
    async getDiagCodeList() {
      // Get APIを呼び出す
      const response = await executeSyncRequest(
        this,
        async () => {
          return await apiService.getCall(
            apiService.OBD,
            `/obd/${this.obdDataId}/measures/${this.measuredId}/dtc`,
            this.operationScreen
          );
        },
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // GET API で正常レスポンスが返ってきた際の内容チェックはデフォルト処理を利用
        undefined,
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する
        (result) => { this.validateErrorResult(result); },
      );
      // 処理失敗時は response が null になるので考慮
      return response ? response.data : null;
    },

    /**
     * その他のエラー発生時の処理を行う
     * @param {string} action 処理内容
     */
    showUnexpectedError() {
      const messages = this.$t('fixed_resource.obd_function_common_message_result_ng_2');
      alert.showError(this, null,
        messages,
        () => {
          // 遷移処理
          pushFunctionList(this, this.$route.params.systemNo);
        });
    },

    /**
     * メモダイアログを表示する
     */
    openNoteModal() {
      this.modalMemo = true;
    },

    /**
     * メモダイアログを閉じる
     */
    closeNoteModal() {
      this.modalMemo = false;
    },

    /**
     * メモ情報の保存
     */
    async memoSave() {
      await this.$refs.memo.save();
    },

    /**
     * メモ情報のクリア
     */
    memoClear: function () {
      this.$refs.memo.clear();
    },
    /* end メモ用の関数一覧 */

    /**
     * 再取得ボタン押下時の処理を行う
     */
    async reGetDTC() {
      const messages = this.$t('individual.message_diag_code_confirm_get_information');
      alert.showConfirmation(this, null,
        messages,
        () => {
          // ダイアグコードの取得
          this.getDiagCodeRequest(true);
        });
    },

    /**
     * カラムを作り直す
     */
    updateColumns() {
      // ダイアグコードレスポンスが空の場合、return
      if (!this.getDiagCodeListResponse) {
        return;
      }
      this.dtcSystemTableColumns = [
        { name: 'status', label: 'fixed_resource.header_vehicle_dtc_table_status' },
        { name: 'dtc', label: 'fixed_resource.header_vehicle_dtc_table_code', originalSortFunc: diagnoseResource.sortFuncCode },
        { name: 'description', label: 'fixed_resource.header_vehicle_dtc_table_description', useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription },
        { name: 'igonCount', label: 'fixed_resource.header_vehicle_dtc_table_igon_counts' },
        { name: 'igonTime', label: 'fixed_resource.header_vehicle_dtc_table_igon_times' },
        { name: 'counterType', label: 'fixed_resource.header_vehicle_dtc_table_type', useResource: true },
        { name: 'ffd', label: 'fixed_resource.header_vehicle_dtc_table_ffd', useButton: true, useResource: true },
      ];
    },

    /**
     * テーブル情報更新処理を行う
     */
    updateDTCTable() {
      const systemNo = this.$route.params.systemNo;
      // システムに紐づく DTC があるかチェックする
      let filteredDtcSystems = [];
      if (this.getDiagCodeListResponse) {
        filteredDtcSystems = this.getDiagCodeListResponse.data.filter(dtc => dtc.system === systemNo);
      }
      // システムNoとシステム名
      const system = this.systems.find(e => e.systemNo === systemNo);
      // 第3引数は使用していないが、今後の追加実装に備えて正しい値を設定する。
      this.dtcListItems = createObdDtcTable(filteredDtcSystems, systemNo, system?.systemName, this);
    },

    /**
     * DTC情報をCSVエクスポートする
     */
    async csvExport() {
      const fileInfo = {};
      let resultInfo = {};
      // 行のフォーマット
      resultInfo = this.dtcListItems.map(item => {
        return {
          ...item,
          status: this.$t(item.status),
          description: this.$t(item.description),
          counterType: this.$t(item.counterType),
          ffd: item.buttonVisible ? this.$t('message.available_ffd') : this.$t('message.no_ffd')
        };
      });
      // 列のフォーマット
      fileInfo.column = this.dtcSystemTableColumns.map(column => {
        if (column.name === 'igonCount') {
          column.label = this.$t('fixed_resource.header_vehicle_dtc_table_igon_counts');
        } else if (column.name === 'igonTime') {
          column.label = this.$t('fixed_resource.header_vehicle_dtc_table_igon_times');
        }
        return column;
      });
      // VINデータのフォーマット
      // Start画面から遷移した場合は、VIN取得をしないため、空文字で出力する。
      const vinData = this.isDisabledMemo ? '' : (this.vehicleInfo?.vehicle?.vin?.vin || '');
      const vinHeader = `"${this.$t('message.label_measured_data_search_vin')}","${vinData}"\n`;
      // 出力形式の設定
      const headers = fileInfo.column.map(column => {
        if (column.name === 'igonCount' || column.name === 'igonTime') {
          return { id: column.name, title: column.label };
        } else {
          return { id: column.name, title: this.$t(column.label) };
        }
      });
      // 説明・故障部位のエスケープ処理
      fileInfo.record = util.escapeDoubleQuotation(resultInfo, 'description');

      let endPoint = '';
      let measuredDataName = '';

      if (!this.isDisabledMemo && this.vehicleInfo.vehicle.project.id !== null && this.measuredId !== null) {
        try {
          this.processing = true;
          // メモボタン活性（パスに"menu"が存在）かつプロジェクトIDおよび計測データIDが存在する場合、計測データ取得APIを指定
          endPoint = '/projects/' + this.vehicleInfo.vehicle.project.id + '/' + this.measuredId;
          // 計測データ名を取得
          measuredDataName = await getMeasuredDataName(this, this.destinationAPI, endPoint, this.operationScreen);
        } finally {
          this.processing = false;
        }
      }
      const fileName = getCsvFileName(
        `${this.$t(this.functionNameKey)}${this.$t(this.obdFunctionLabelKey)}`,
        measuredDataName,
        this.isDisabledMemo ? '' : (this.vehicleInfo?.vehicle?.vin?.vin || '')
      );

      // CSVファイルエクスポート処理
      const data = vinHeader + util.createCsvData(headers, fileInfo.record);
      // CSVファイルエクスポート処理
      util.csvFileExport(data, fileName);
    },

    // vue warn 回避
    onClickAddDtcReadinessCode() { return; },
    deleteRowReadinessCode() { return; },
    allDeleteReadinessCode() { return; },
    readinessCodeCsvExport() { return; },
    onClickDecisionReadinessCode() { return; },
    clickFFD() { return; },
    deleteDTC() { return; }
  },

  computed: {
    ...mapState({
      vehicleInfo: state => state.vehicle.vehicleInfo,
      // メニューサポートの情報
      language: state => state.systems.language,
      // クラウドコネクターの接続状況
      netAppId: state => state.netAppConnector.pairingAppId,
      cachedFFDs: state => state.cache.ffdStores,
      // OBD Data ID の取得結果
      obdDataId: state => state.obd.obdDataId,
      systems: state => state.obd.systems,
    }),

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },

    /** プロジェクトID */
    projectId() {
      return this.vehicleInfo?.vehicle?.project?.id || '';
    }
  },

  /**
   * DOM 生成後の処理を行う
   */
  mounted() {
    this.mountedLogic();
  },
};